import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { menuPreferencesAPI } from '../api';
import { DotsSixVertical, ArrowClockwise } from '@phosphor-icons/react';

/**
 * MenuPreferencesDialog
 *
 * Allows the current user to reorder the sidebar groups via drag & drop.
 * Persists the order via /api/menu-preferences (per user).
 *
 * Props:
 * - open (bool)
 * - onOpenChange (fn)
 * - groups: [{ key, label }]  (only visible-to-user groups)
 * - initialOrder: [key, ...]
 * - onSaved: (newOrder) => void
 */
export default function MenuPreferencesDialog({
  open,
  onOpenChange,
  groups,
  initialOrder,
  onSaved,
}) {
  const [order, setOrder] = useState(initialOrder || groups.map(g => g.key));
  const [saving, setSaving] = useState(false);
  const dragKey = useRef(null);

  useEffect(() => {
    if (open) {
      setOrder(initialOrder || groups.map(g => g.key));
    }
  }, [open, initialOrder, groups]);

  const groupLabel = (key) => groups.find(g => g.key === key)?.label || key;

  const handleDragStart = (e, key) => {
    dragKey.current = key;
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', key);
    } catch {}
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch {}
  };

  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    const sourceKey = dragKey.current || e.dataTransfer.getData('text/plain');
    if (!sourceKey || sourceKey === targetKey) return;
    setOrder(prev => {
      const next = [...prev];
      const sIdx = next.indexOf(sourceKey);
      const tIdx = next.indexOf(targetKey);
      if (sIdx < 0 || tIdx < 0) return prev;
      next.splice(sIdx, 1);
      next.splice(tIdx, 0, sourceKey);
      return next;
    });
    dragKey.current = null;
  };

  const moveBy = (key, delta) => {
    setOrder(prev => {
      const next = [...prev];
      const idx = next.indexOf(key);
      const newIdx = idx + delta;
      if (idx < 0 || newIdx < 0 || newIdx >= next.length) return prev;
      const [item] = next.splice(idx, 1);
      next.splice(newIdx, 0, item);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await menuPreferencesAPI.update({ group_order: order });
      toast.success('Menú personalizado guardado');
      onSaved?.(resp.data?.group_order || order);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const resp = await menuPreferencesAPI.reset();
      const defaultOrder = resp.data?.group_order;
      setOrder(defaultOrder);
      toast.success('Orden por defecto restaurado');
      onSaved?.(defaultOrder);
    } catch (e) {
      toast.error('No se pudo restablecer');
    } finally {
      setSaving(false);
    }
  };

  // Visible-to-user groups (respect current order)
  const visibleKeys = new Set(groups.map(g => g.key));
  const displayOrder = order.filter(k => visibleKeys.has(k));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="menu-prefs-dialog">
        <DialogHeader>
          <DialogTitle>Personalizar orden del menú</DialogTitle>
          <DialogDescription>
            Arrastra los grupos para reordenar tu menú lateral. Este orden solo aplica a tu usuario.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2" data-testid="menu-prefs-list">
          {displayOrder.map((key, idx) => (
            <div
              key={key}
              draggable
              onDragStart={(e) => handleDragStart(e, key)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, key)}
              className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 cursor-grab active:cursor-grabbing"
              data-testid={`menu-prefs-item-${key}`}
            >
              <DotsSixVertical size={18} className="text-slate-400" />
              <span className="flex-1 text-sm font-medium text-slate-900">{groupLabel(key)}</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={idx === 0}
                  onClick={() => moveBy(key, -1)}
                  data-testid={`menu-prefs-up-${key}`}
                  aria-label="Subir"
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={idx === displayOrder.length - 1}
                  onClick={() => moveBy(key, 1)}
                  data-testid={`menu-prefs-down-${key}`}
                  aria-label="Bajar"
                >
                  ↓
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleReset} disabled={saving} data-testid="menu-prefs-reset">
            <ArrowClockwise size={16} className="mr-1" />
            Restablecer
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="menu-prefs-save">
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
